/**
 * Sealift (Phases 7b and 13c-2): where an invasion sails from, where it lands, and which
 * ships can carry it there. Shared by the soldiers' war plan (campaign.ts) and the
 * ships' orders (military.ts).
 */

import type { Content } from '../../../content/schema/index.js';
import { citiesOf, cityIndex } from '../cities/index.js';
import { isCoastalCity } from '../cities/production.js';
import { findPath } from '../path/astar.js';
import { neighborsByDirection } from '../state/grid.js';
import { wrappedDistance } from '../state/start.js';
import type { GameState, Id, Unit } from '../state/types.js';
import { unitDef, unitIndex, unitsOf } from '../units/index.js';
import { pathOptions } from '../units/move.js';

/** The coastal city of this civilisation nearest to a target: where an invasion sails from. */
export function stagingPort(state: GameState, content: Content, civId: Id, target: number): number | null {
  let best: number | null = null;
  let bestDistance = Infinity;
  for (const city of citiesOf(state, civId)) {
    if (!isCoastalCity(content, state.map, city)) continue;
    const at = cityIndex(state.map, city);
    const distance = wrappedDistance(state.map, at, target);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = at;
    }
  }
  return best;
}

/** Water tiles this ship could reach near a target, nearest the target first. */
export function landingWater(state: GameState, content: Content, ship: Unit, target: number): number | null {
  const def = unitDef(content, ship);
  const here = unitIndex(state.map, ship);
  const candidates: number[] = [];
  const ring = new Int32Array(6);
  // Water next to land that is next to the target: one step ashore, one step to the walls.
  neighborsByDirection(state.map, target, ring);
  const shore = [...ring].filter((i) => i !== -1 && !content.terrain.get(state.map.terrain[i]!).water);
  shore.push(target);
  for (const land of shore) {
    neighborsByDirection(state.map, land, ring);
    for (let d = 0; d < 6; d++) {
      const w = ring[d]!;
      if (w === -1 || candidates.includes(w)) continue;
      const terrain = content.terrain.get(state.map.terrain[w]!);
      if (!terrain.water || (def.coastal && terrain.deep)) continue;
      candidates.push(w);
    }
  }
  candidates.sort((a, b) => wrappedDistance(state.map, a, target) - wrappedDistance(state.map, b, target) || a - b);
  for (const w of candidates) {
    if (w === here) return w;
    const route = findPath(pathOptions(state, content, ship, w), here, w);
    if (route && route.length > 0) return w;
  }
  return null;
}

/**
 * This civilisation's ships that can carry soldiers to a target: transports that could
 * reach water beside it. A coastal galley cannot cross the ocean, however many it has.
 */
export function usableTransports(state: GameState, content: Content, civId: Id, target: number): Unit[] {
  return unitsOf(state, civId).filter((u) => {
    const def = unitDef(content, u);
    return def.domain === 'sea' && def.capacity > 0 && landingWater(state, content, u, target) !== null;
  });
}

/** Room aboard those ships, in soldiers. */
export function liftFor(state: GameState, content: Content, civId: Id, target: number): number {
  return usableTransports(state, content, civId, target).reduce((sum, u) => sum + unitDef(content, u).capacity, 0);
}
